import { Package, TrendingUp, Truck, MessageSquare, CheckCircle, Clock, ThumbsUp, Bell, AlertCircle, Info, UserPlus, X, XCircle, RefreshCw, Pencil, CircleCheck, Star, Users, Trash2, LayoutGrid } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import StatsCard from "@/components/StatsCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { Load, Truck as TruckType, User, LoadItem } from "@shared/schema";
import { useLocation } from "wouter";
import { useUser } from "@/contexts/UserContext";
import { apiRequest, queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VerificationBanner } from "@/components/VerificationBanner";
import { AnalyticsDashboard } from "@/components/AnalyticsDashboard";
import LoadDetailModal from "@/components/LoadDetailModal";
import TruckDetailModal from "@/components/TruckDetailModal";
import EditLoadModal from "@/components/EditLoadModal";
import { useState, useRef, useEffect } from "react";
import { useSocket } from "@/contexts/SocketContext";

interface LoadThreadSummary {
  threadId: string | null;
  threadPhase: string | null;
  partnerId: string;
  loadId: string | null;
}

interface UpvoteStatus {
  loadId: string;
  userHasUpvoted: boolean;
  receivedUpvote: boolean;
  bothSubmitted: boolean;
  revealed: boolean;
  revealDate: string | null;
  canUpvote: boolean;
}

interface PendingNotification {
  type: "confirm" | "upvote" | "upvote_received" | "pending_rating";
  loadId: string;
  route: string;
  message: string;
  daysRemaining?: number;
}

interface ExternalCarrierInfo {
  id: string;
  companyName: string;
  contactName?: string;
  dotNumber?: string;
  mcNumber?: string;
  partyType: string;
}

interface LoadWithExternalCarrier extends Load {
  externalCarrier?: ExternalCarrierInfo | null;
}

function LoadCard({ load, currentUser, onUpvote, upvotingLoadId, onClick, onReactivate, onEdit, onDelete, onAddPartner, reactivatingLoadId, loadThreads, onNavigate }: { 
  load: LoadWithExternalCarrier; 
  currentUser?: User;
  onUpvote?: (loadId: string, toUserId: string) => void;
  upvotingLoadId?: string;
  onClick?: () => void;
  onReactivate?: (loadId: string) => void;
  onEdit?: (load: LoadWithExternalCarrier) => void;
  onDelete?: (loadId: string) => void;
  onAddPartner?: (loadId: string) => void;
  reactivatingLoadId?: string;
  loadThreads?: LoadThreadSummary[];
  onNavigate?: (url: string) => void;
}) {
  const formatDate = (dateString: Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const { data: upvoteStatus } = useQuery<UpvoteStatus>({
    queryKey: ['/api/loads', load.id, 'upvote-status'],
    enabled: load.status === "Completed",
  });

  const isUserInvolved = currentUser && (load.userId === currentUser.id || load.claimedByUserId === currentUser.id);

  const getOtherPartyId = () => {
    if (!currentUser) return null;
    if (load.userId === currentUser.id) return load.claimedByUserId;
    return load.userId;
  };

  const isOwnLoad = currentUser && load.userId === currentUser.id;
  const isClaimedByMe = currentUser && load.claimedByUserId === currentUser.id;

  // Thread state for smart buttons
  const activeShipmentThread = loadThreads?.find(t => t.threadPhase === 'active_shipment') ?? null;
  const hasAnyThread = (loadThreads?.length ?? 0) > 0;
  const pendingBidCount = loadThreads?.filter(t => t.threadPhase === 'quote_submitted').length ?? 0;
  const hasBids = pendingBidCount > 0 || hasAnyThread;

  const getStatusBadge = () => {
    if (load.status === "Completed") {
      return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
    }
    if (load.status === "Closed") {
      return <Badge variant="secondary" data-testid={`badge-load-closed-${load.id}`}><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
    }
    if (load.externalCarrierId || load.externalCarrier) {
      return <Badge variant="secondary"><UserPlus className="w-3 h-3 mr-1" />External</Badge>;
    }
    if (activeShipmentThread) {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">In progress</span>;
    }
    if (pendingBidCount > 0) {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">{pendingBidCount} {pendingBidCount === 1 ? 'bid' : 'bids'} waiting</span>;
    }
    return <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">Available</span>;
  };

  const getRevealTimeRemaining = () => {
    if (!upvoteStatus?.revealDate) return null;
    const revealDate = new Date(upvoteStatus.revealDate);
    const now = new Date();
    const diff = revealDate.getTime() - now.getTime();
    if (diff <= 0) return "Reveal pending";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return `${days} day${days !== 1 ? 's' : ''} until reveal`;
  };

  return (
    <Card
      className={`p-3 hover-elevate transition-all cursor-pointer${pendingBidCount > 0 && !activeShipmentThread ? ' border-2 border-blue-200' : ''}`}
      data-testid={`card-load-${load.id}`}
      onClick={onClick}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            {load.loadIdentifier && (
              <Badge variant="outline" className="font-mono text-xs mb-1" data-testid={`text-load-id-${load.id}`}>
                {load.loadIdentifier}
              </Badge>
            )}
            <h3 className="font-semibold" data-testid="text-route">
              {load.origin} → {load.destination}
            </h3>
          </div>
          {getStatusBadge()}
        </div>
        <div className="text-base md:text-sm text-muted-foreground space-y-1">
          <p data-testid="text-weight">Weight: {load.weight.toLocaleString()} lbs</p>
          <p data-testid="text-pickup">Pickup: {formatDate(load.pickupDate)}</p>
        </div>


        {load.externalCarrier && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              <UserPlus className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">External Carrier:</span>
              <span className="font-medium" data-testid={`text-external-carrier-${load.id}`}>
                {load.externalCarrier.companyName}
              </span>
            </div>
            {load.externalCarrier.dotNumber && (
              <p className="text-xs text-muted-foreground ml-6">
                DOT: {load.externalCarrier.dotNumber}
              </p>
            )}
          </div>
        )}

        {load.status === "Completed" && isUserInvolved && upvoteStatus && (
          <div className="pt-2 border-t space-y-2">
            <div className="flex items-center gap-2 text-base md:text-sm">
              <ThumbsUp className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Upvote Status</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" data-testid="button-upvote-info" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">
                    Upvotes are endorsements from members you've worked with. They're revealed after both parties submit or after 7 days. 
                    <strong> Upvoting is optional</strong> — only upvote if you had a positive experience.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {upvoteStatus.revealed ? (
              <div className="space-y-1">
                {upvoteStatus.receivedUpvote && (
                  <div className="flex items-center gap-2 text-base md:text-sm text-green-600">
                    <ThumbsUp className="w-4 h-4" />
                    You received an upvote!
                  </div>
                )}
                {upvoteStatus.userHasUpvoted && (
                  <div className="text-base md:text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Your upvote has been revealed
                  </div>
                )}
              </div>
            ) : (
              <>
                {upvoteStatus.userHasUpvoted ? (
                  <div className="text-base md:text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Your upvote is pending. {getRevealTimeRemaining()}
                  </div>
                ) : upvoteStatus.canUpvote && onUpvote && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground" data-testid="text-upvote-explainer">
                      Had a good experience? Give an upvote to recommend this member. 
                      <span className="font-medium"> This is optional</span> — only upvote if you'd work with them again.
                    </p>
                    <Button 
                      size="sm" 
                      variant="secondary"
                      className="w-full" 
                      onClick={(e) => {
                        e.stopPropagation();
                        const otherPartyId = getOtherPartyId();
                        if (otherPartyId) onUpvote(load.id, otherPartyId);
                      }}
                      disabled={upvotingLoadId === load.id}
                      data-testid={`button-upvote-${load.id}`}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {upvotingLoadId === load.id ? "Submitting..." : "Give Upvote"}
                    </Button>
                  </div>
                )}
                {!upvoteStatus.userHasUpvoted && getRevealTimeRemaining() && (
                  <div className="text-xs text-muted-foreground text-center">
                    {getRevealTimeRemaining()}
                  </div>
                )}
              </>
            )}
          </div>
        )}


        {/* Claimed Load actions (Carrier/Broker who claimed someone else's load) */}
        {isClaimedByMe && !isOwnLoad && load.status !== "Completed" && activeShipmentThread?.threadId && onNavigate && (
          <div className="pt-2 border-t">
            <Button
              size="sm"
              className="w-full"
              onClick={(e) => { e.stopPropagation(); onNavigate(`/shipments?load=${load.id}&thread=${activeShipmentThread.threadId}`); }}
              data-testid={`button-go-to-shipment-${load.id}`}
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1" />
              Go to Shipment
            </Button>
          </div>
        )}

        {/* My Load actions (Shipper/Broker who posted the load) */}
        {isOwnLoad && (
          <div className="pt-2 border-t">
            {load.status === 'Completed' ? (
              <button
                onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(`/loads/${load.id}/bids`); }}
                className="w-full text-sm font-medium underline text-blue-600 bg-transparent border-0 cursor-pointer py-1"
                data-testid={`button-view-bid-summary-${load.id}`}
              >
                View bid summary
              </button>
            ) : activeShipmentThread?.threadId ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(`/shipments?load=${load.id}&thread=${activeShipmentThread.threadId}`); }}
                  className="flex-1 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  data-testid={`button-view-active-shipment-${load.id}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Go to shipment
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(`/loads/${load.id}/bids`); }}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                  title="View bids"
                  data-testid={`button-view-bids-active-${load.id}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                {onDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(load.id); }}
                    className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete"
                    data-testid={`button-delete-load-${load.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : pendingBidCount > 0 ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(`/loads/${load.id}/bids`); }}
                  className="flex-1 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  data-testid={`button-view-bids-${load.id}`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Compare bids
                </button>
                {onAddPartner && load.status !== 'Closed' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAddPartner(load.id); }}
                    className="p-2 rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors"
                    title="Add partner"
                    data-testid={`button-add-partner-load-${load.id}`}
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                )}
                {onEdit && !load.claimedByUserId && load.status !== 'Closed' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(load); }}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    title="Edit"
                    data-testid={`button-edit-load-${load.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {onDelete && !load.claimedByUserId && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(load.id); }}
                    className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete"
                    data-testid={`button-delete-load-${load.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : load.status === 'Closed' ? (
              <div className="flex items-center gap-2">
                {onReactivate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onReactivate(load.id); }}
                    className="flex-1 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    disabled={reactivatingLoadId === load.id}
                    data-testid={`button-reactivate-load-${load.id}`}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${reactivatingLoadId === load.id ? 'animate-spin' : ''}`} />
                    {reactivatingLoadId === load.id ? 'Reactivating...' : 'Reactivate'}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {onAddPartner && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAddPartner(load.id); }}
                    className="flex-1 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                    data-testid={`button-add-partner-load-${load.id}`}
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Add partner
                  </button>
                )}
                {onEdit && !load.claimedByUserId && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(load); }}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    title="Edit"
                    data-testid={`button-edit-load-${load.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {onDelete && !load.claimedByUserId && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(load.id); }}
                    className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete"
                    data-testid={`button-delete-load-${load.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function TruckCardSmall({ truck, onClick }: { 
  truck: TruckType; 
  onClick?: () => void;
}) {
  const formatDate = (dateString: Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const isClosed = truck.status === "Closed";

  return (
    <Card 
      className={`p-3 hover-elevate transition-all ${onClick ? 'cursor-pointer' : ''}`} 
      onClick={onClick}
      data-testid={`card-truck-${truck.id}`}
    >
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold" data-testid="text-route">
            {truck.origin} → {truck.destination}
          </h3>
          {isClosed && (
            <Badge variant="secondary" className="text-xs" data-testid="badge-truck-closed">
              Closed
            </Badge>
          )}
        </div>
        <div className="text-base md:text-sm text-muted-foreground space-y-1">
          <p data-testid="text-capacity">Capacity: {truck.capacity.toLocaleString()} lbs</p>
          <p data-testid="text-available">Available: {formatDate(truck.availableFrom)}</p>
        </div>
      </div>
    </Card>
  );
}

interface PublicUserInfo {
  id: string;
  company: string;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
}

interface LoadWithPoster extends Load {
  items?: LoadItem[];
  poster: PublicUserInfo | null;
}

interface TruckWithCarrier extends TruckType {
  carrier: PublicUserInfo | null;
}

interface PreferredPartner {
  id: string;
  email: string;
  company: string;
  userType: string;
}

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const { userName, userType } = useUser();
  const { toast } = useToast();
  const [selectedLoad, setSelectedLoad] = useState<LoadWithPoster | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<TruckWithCarrier | null>(null);
  const [truckModalOpen, setTruckModalOpen] = useState(false);
  const [showClosedTrucks, setShowClosedTrucks] = useState(false);
  const [showPastLoads, setShowPastLoads] = useState(false);
  const [showPastClaims, setShowPastClaims] = useState(false);
  const [editingLoad, setEditingLoad] = useState<(Load & { items?: LoadItem[] }) | null>(null);
  const [editLoadModalOpen, setEditLoadModalOpen] = useState(false);
  const [partnersBannerDismissed, setPartnersBannerDismissed] = useState(false);
  const [deleteLoadDialogOpen, setDeleteLoadDialogOpen] = useState(false);
  const [deleteLoadTargetId, setDeleteLoadTargetId] = useState<string | null>(null);
  const [addPartnerModalOpen, setAddPartnerModalOpen] = useState(false);
  const [addPartnerLoadId, setAddPartnerLoadId] = useState<string | null>(null);
  const [addPartnerName, setAddPartnerName] = useState("");
  const [addPartnerEmail, setAddPartnerEmail] = useState("");
  const [addPartnerType, setAddPartnerType] = useState<"carrier" | "broker">("carrier");
  const [addPartnerLoading, setAddPartnerLoading] = useState(false);
  const [addPartnerSelectedId, setAddPartnerSelectedId] = useState<string | null>(null);

  const { data: currentUser } = useQuery<User>({
    queryKey: ['/api/auth/user'],
  });

  const { data: verificationSetting } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });

  const isUserVerified = verificationSetting?.verificationRequired === false || currentUser?.verificationStatus === "verified";

  const userId = currentUser?.id || "temp-user-id";

  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data: any) => {
      if (data?.loadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
      }
    };

    const handleNewNotification = () => {
      queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
    };

    socket.on('new_message', handleNewMessage);
    socket.on('new_notification', handleNewNotification);
    socket.on('load_confirmed', handleNewNotification);
    socket.on('quote_declined', handleNewNotification);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('new_notification', handleNewNotification);
      socket.off('load_confirmed', handleNewNotification);
      socket.off('quote_declined', handleNewNotification);
    };
  }, [socket, userId]);

  const { data: allLoads } = useQuery<Load[]>({ queryKey: ["/api/loads"] });
  const { data: userLoads } = useQuery<Load[]>({ 
    queryKey: ['/api/loads/user', userId],
    enabled: !!currentUser,
    refetchOnWindowFocus: true,
  });
  const { data: claimedLoads } = useQuery<Load[]>({ 
    queryKey: ['/api/loads/claimed/me'],
    enabled: !!currentUser,
  });
  const { data: threadsList } = useQuery<LoadThreadSummary[]>({ 
    queryKey: ['/api/messages/threads'],
    enabled: !!currentUser,
  });
  const { data: allTrucks } = useQuery<TruckType[]>({ queryKey: ["/api/trucks"] });
  const { data: userTrucks } = useQuery<TruckType[]>({ 
    queryKey: ['/api/trucks/user', userId],
    enabled: !!currentUser,
  });
  const { data: notifications } = useQuery<PendingNotification[]>({ 
    queryKey: ['/api/notifications/pending'],
    enabled: !!currentUser,
  });

  const { data: preferredPartners } = useQuery({
    queryKey: ["/api/preferred-partners"],
    enabled: userType === "Shipper",
  });

  interface CompletedShipmentItem {
    loadId: string;
    loadIdentifier: string | null;
    route: string;
    completedAt: Date | string | null;
    otherPartyName: string;
    otherPartyType: string;
    rating: number | null;
    threadId: string;
  }
  interface CompletedShipmentsData {
    shipments: CompletedShipmentItem[];
    summary: { total: number; averageGiven: number | null };
  }
  const { data: completedShipmentsData } = useQuery<CompletedShipmentsData>({
    queryKey: ['/api/completed-shipments'],
    enabled: !!currentUser,
  });

  const upvoteMutation = useMutation({
    mutationFn: async ({ loadId, toUserId }: { loadId: string; toUserId: string }) => {
      const response = await apiRequest("POST", `/api/loads/${loadId}/upvote`, { toUserId });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      
      if (data.revealed) {
        toast({
          title: "Upvotes Revealed!",
          description: data.message,
        });
      } else {
        toast({
          title: "Upvote Submitted",
          description: data.message,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Upvote Failed",
        description: error.message || "There was an error submitting your upvote.",
        variant: "destructive",
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async ({ type, loadId }: { type: string; loadId: string }) => {
      const response = await apiRequest("POST", "/api/notifications/pending/dismiss", { type, loadId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/pending'] });
    },
    onError: (error: any) => {
      toast({
        title: "Dismiss Failed",
        description: error.message || "There was an error dismissing the item.",
        variant: "destructive",
      });
    },
  });

  const handleDismissNotification = (type: string, loadId: string) => {
    dismissMutation.mutate({ type, loadId });
  };

  const activeLoads = userLoads?.filter(l => l.status === 'Available').length || 0;
  const activeTrucks = userTrucks?.filter(t => t.status === 'Available').length || 0;
  const totalListings = (userLoads?.length || 0) + (userTrucks?.length || 0);
  const claimedCount = claimedLoads?.length || 0;
  const completedCount = (userLoads?.filter(l => l.status === 'Completed').length || 0) + 
                         (claimedLoads?.filter(l => l.status === 'Completed').length || 0);

  // Shipper-specific stats
  const activeLoadsCount = userLoads?.filter(l => l.status === 'Available').length || 0;
  const activeThreadsCount = threadsList?.filter((t: any) => 
    !['completed', 'auto_closed'].includes(t.threadPhase)
  ).length || 0;
  const hasUnreadMessages = threadsList?.some((t: any) => !t.lastMessageReadAt || 
    (t.lastMessageAt && new Date(t.lastMessageAt) > new Date(t.lastMessageReadAt))) || false;
  const activePartnersCount = (preferredPartners as any[])?.length || 0;
  const showPartnersBanner = userType === 'Shipper' && 
    !partnersBannerDismissed && 
    activePartnersCount === 0;

  const getSubtitle = () => {
    if (userType === 'Shipper') return 'Post a shipment, notify your partners, and compare bids — all in one place.';
    if (userType === 'Carrier') return 'Manage your truck space and find loads';
    return 'Manage loads and truck space for your clients';
  };

  const handleUpvote = (loadId: string, toUserId: string) => {
    upvoteMutation.mutate({ loadId, toUserId });
  };

  const handleLoadClick = async (load: Load) => {
    try {
      const response = await fetch(`/api/loads/${load.id}`);
      if (response.ok) {
        const loadWithItems = await response.json();
        setSelectedLoad({ ...loadWithItems, poster: null } as LoadWithPoster);
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to fetch load details:", error);
      setSelectedLoad({ ...load, poster: null } as LoadWithPoster);
      setModalOpen(true);
    }
  };

  const handleTruckClick = (truck: TruckType) => {
    // For dashboard, the user owns the truck, so carrier info is the current user
    const truckWithCarrier: TruckWithCarrier = {
      ...truck,
      carrier: currentUser ? {
        id: currentUser.id,
        company: currentUser.company || "Unknown Company",
        dotNumber: currentUser.dotNumber || undefined,
        mcNumber: currentUser.mcNumber || undefined,
        adminVerified: currentUser.verificationStatus === "verified",
        upvoteCount: currentUser.upvoteCount || 0,
        completedShipments: currentUser.completedShipments || 0,
        userType: currentUser.userType || "Carrier"
      } : null
    };
    setSelectedTruck(truckWithCarrier);
    setTruckModalOpen(true);
  };

  const handleTruckStatusChange = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/trucks/user', userId] });
  };

  const closeLoadMutation = useMutation({
    mutationFn: async (loadId: string) => {
      await apiRequest("PATCH", `/api/loads/${loadId}/close`);
    },
    onSuccess: () => {
      toast({ title: "Load Closed", description: "This load is no longer visible to carriers." });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to close load.", variant: "destructive" });
    },
  });

  const deleteLoadMutation = useMutation({
    mutationFn: async (loadId: string) => {
      const res = await fetch(`/api/loads/${loadId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...getCsrfHeaders("DELETE") },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete load");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/loads/user"] });
      toast({ title: "Load deleted successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting load",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDeleteLoad = (loadId: string) => {
    setDeleteLoadTargetId(loadId);
    setDeleteLoadDialogOpen(true);
  };

  const confirmDeleteLoad = () => {
    if (deleteLoadTargetId) {
      deleteLoadMutation.mutate(deleteLoadTargetId);
    }
    setDeleteLoadDialogOpen(false);
    setDeleteLoadTargetId(null);
  };

  const handleAddPartnerToLoad = (loadId: string) => {
    setAddPartnerLoadId(loadId);
    setAddPartnerModalOpen(true);
  };

  const handleSubmitPartnerToLoad = async () => {
    if (!addPartnerLoadId) return;
    setAddPartnerLoading(true);

    const resetState = () => {
      setAddPartnerModalOpen(false);
      setAddPartnerName("");
      setAddPartnerEmail("");
      setAddPartnerType("carrier");
      setAddPartnerLoadId(null);
      setAddPartnerSelectedId(null);
    };

    try {
      if (addPartnerSelectedId) {
        // Existing partner — notify them about this specific load
        const res = await fetch(`/api/loads/${addPartnerLoadId}/notify-partner`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
          body: JSON.stringify({ partnerId: addPartnerSelectedId }),
        });
        const data = await res.json();
        if (res.ok) {
          toast({
            title: "Partner notified",
            description: `${addPartnerName} has been notified about this load.`,
          });
          resetState();
          queryClient.invalidateQueries({ queryKey: ["/api/partner-invites"] });
        } else {
          toast({ title: "Error", description: ["Cancelled", "Suspended"].includes(data.membershipTier) ? data.message : data.error, variant: "destructive" });
        }
      } else {
        // New partner — send invite via partner-invites
        if (!addPartnerEmail || !addPartnerName) return;
        const res = await fetch("/api/partner-invites", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
          body: JSON.stringify({
            email: addPartnerEmail,
            name: addPartnerName,
            partnerType: addPartnerType,
            loadId: addPartnerLoadId,
          }),
        });
        const data = await res.json();
        if (res.ok) {
          toast({
            title: "Invitation sent",
            description: `An invitation has been sent to ${addPartnerEmail}.`,
          });
          resetState();
          queryClient.invalidateQueries({ queryKey: ["/api/partner-invites"] });
        } else {
          toast({ title: "Error", description: ["Cancelled", "Suspended"].includes(data.membershipTier) ? data.message : data.error, variant: "destructive" });
        }
      }
    } catch {
      toast({ title: "Error", description: "Failed to add partner", variant: "destructive" });
    } finally {
      setAddPartnerLoading(false);
    }
  };

  const reactivateLoadMutation = useMutation({
    mutationFn: async (loadId: string) => {
      await apiRequest("PATCH", `/api/loads/${loadId}/reactivate`);
    },
    onSuccess: () => {
      toast({ title: "Load Reactivated", description: "This load is now visible to carriers again." });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reactivate load.", variant: "destructive" });
    },
  });

  const handleCloseLoad = (loadId: string) => {
    closeLoadMutation.mutate(loadId);
  };

  const handleReactivateLoad = (loadId: string) => {
    reactivateLoadMutation.mutate(loadId);
  };


  const handleEditLoad = async (load: Load) => {
    try {
      const response = await fetch(`/api/loads/${load.id}`);
      if (response.ok) {
        const loadWithItems = await response.json();
        setEditingLoad(loadWithItems);
      } else {
        setEditingLoad(load);
      }
      setEditLoadModalOpen(true);
    } catch {
      setEditingLoad(load);
      setEditLoadModalOpen(true);
    }
  };

  const handleEditLoadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
    queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
  };

  // Build a map from loadId -> thread summaries for efficient lookup
  const threadsByLoadId = (() => {
    const map = new Map<string, LoadThreadSummary[]>();
    if (threadsList) {
      for (const thread of threadsList) {
        if (thread.loadId) {
          const existing = map.get(thread.loadId) || [];
          existing.push(thread);
          map.set(thread.loadId, existing);
        }
      }
    }
    return map;
  })();

  const handleNavigateToShipment = (url: string) => {
    setLocation(url);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <VerificationBanner />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" data-testid="text-welcome">
          Welcome back, {userName}!
        </h1>
        <p className="text-muted-foreground">
          {getSubtitle()}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {userType === 'Shipper' && (
          <>
            <StatsCard
              icon={Package}
              label="Active Loads"
              value={activeLoadsCount}
              onClick={() => document.getElementById('section-my-loads')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="stat-active-loads"
            />
            <div className="relative">
              <StatsCard
                icon={MessageSquare}
                label="Messages"
                value={activeThreadsCount}
                onClick={() => setLocation('/shipments')}
                data-testid="stat-messages"
              />
              {hasUnreadMessages && (
                <span className="absolute top-3 right-3 w-3 h-3 bg-red-500 rounded-full" data-testid="indicator-unread-messages" />
              )}
            </div>
            <StatsCard
              icon={Users}
              label="My Partners"
              value={activePartnersCount}
              onClick={() => setLocation('/preferred-partners')}
              data-testid="stat-my-partners"
            />
          </>
        )}
        {userType === 'Broker' && (
          <StatsCard
            icon={Package}
            label="My Active Loads"
            value={activeLoads}
            onClick={() => document.getElementById('section-my-loads')?.scrollIntoView({ behavior: 'smooth' })}
          />
        )}
        {(userType === 'Carrier' || userType === 'Broker') && (
          <>
            <StatsCard 
              icon={Truck} 
              label="My Active Trucks" 
              value={activeTrucks} 
              onClick={() => document.getElementById('section-my-trucks')?.scrollIntoView({ behavior: 'smooth' })}
            />
            <StatsCard 
              icon={Package} 
              label="Claimed Loads" 
              value={claimedCount} 
              onClick={() => document.getElementById('section-claimed-loads')?.scrollIntoView({ behavior: 'smooth' })}
            />
          </>
        )}
        {userType !== 'Shipper' && (
          <StatsCard 
            icon={CheckCircle} 
            label="Completed Shipments" 
            value={completedCount} 
            onClick={() => document.getElementById('section-analytics')?.scrollIntoView({ behavior: 'smooth' })}
          />
        )}
        {userType !== 'Shipper' && (
          <StatsCard 
            icon={MessageSquare} 
            label="Messages" 
            value={0} 
            onClick={() => setLocation('/shipments')}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {(userType === 'Shipper' || userType === 'Broker') && (
          <div id="section-my-loads">
            <div className="border-l-4 border-blue-500 pl-3 mb-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-xl font-semibold">My loads</h2>
                <div className="flex items-center gap-2">
                  {userLoads && userLoads.some(l => l.status === "Completed" || l.status === "Closed") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPastLoads(!showPastLoads)}
                      data-testid="button-toggle-completed-loads"
                    >
                      {showPastLoads ? "Hide Past" : "Show Past"}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="button-post-load"
                    onClick={() => setLocation('/post-load')}
                  >
                    Post Load →
                  </Button>
                </div>
              </div>
            </div>
            
            {(() => {
              const activeLoads = userLoads?.filter(l => l.status !== "Completed" && l.status !== "Closed") || [];
              const pastLoads = userLoads?.filter(l => l.status === "Completed" || l.status === "Closed") || [];
              const displayLoads = showPastLoads ? userLoads : activeLoads;
              
              if (displayLoads && displayLoads.length > 0) {
                return (
                  <div className="space-y-4">
                    {displayLoads.slice(0, 5).map(load => (
                      <LoadCard 
                        key={load.id} 
                        load={load} 
                        currentUser={currentUser}
                        onUpvote={handleUpvote}
                        upvotingLoadId={upvoteMutation.isPending ? upvoteMutation.variables?.loadId : undefined}
                        onClick={() => handleLoadClick(load)}
                        onReactivate={handleReactivateLoad}
                        onEdit={handleEditLoad}
                        onDelete={handleDeleteLoad}
                        onAddPartner={handleAddPartnerToLoad}
                        reactivatingLoadId={reactivateLoadMutation.isPending ? reactivateLoadMutation.variables : undefined}
                        loadThreads={threadsByLoadId.get(load.id) || []}
                        onNavigate={handleNavigateToShipment}
                      />
                    ))}
                    {pastLoads.length > 0 && !showPastLoads && (
                      <div className="flex items-center gap-2 text-base md:text-sm text-muted-foreground">
                        <span>+ {pastLoads.length} past load{pastLoads.length > 1 ? "s" : ""} not shown</span>
                        <span 
                          className="cursor-pointer underline underline-offset-2"
                          onClick={() => setLocation('/history')}
                          data-testid="link-view-history-loads"
                        >
                          View History
                        </span>
                      </div>
                    )}
                    {showPastLoads && displayLoads.length > 5 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => setLocation('/history')}
                        data-testid="button-view-all-loads"
                      >
                        View All in History ({displayLoads.length} total)
                      </Button>
                    )}
                  </div>
                );
              } else if (activeLoads.length === 0 && pastLoads.length > 0) {
                return (
                  <Card className="p-6 text-center">
                    <p className="text-muted-foreground mb-2">All loads are completed or closed</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowPastLoads(true)}
                      data-testid="button-view-completed"
                    >
                      View Past Loads
                    </Button>
                  </Card>
                );
              } else {
                return (
                  <Card className="p-8 text-center">
                    <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-semibold mb-2">No loads posted yet</h3>
                    <p className="text-base md:text-sm text-muted-foreground mb-4">Start by posting your first load</p>
                    <Button onClick={() => setLocation('/post-load')} data-testid="button-create-load">
                      Post a Load
                    </Button>
                  </Card>
                );
              }
            })()}
          </div>
        )}

        {(userType === 'Carrier' || userType === 'Broker') && (
          <>
            <div id="section-claimed-loads">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <h2 className="text-xl font-semibold">Claimed Loads</h2>
                <div className="flex items-center gap-2">
                  {claimedLoads && claimedLoads.some(l => l.status === "Completed" || l.status === "Closed") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPastClaims(!showPastClaims)}
                      data-testid="button-toggle-completed-claims"
                    >
                      {showPastClaims ? "Hide Past" : "Show Past"}
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    data-testid="button-find-loads"
                    onClick={() => setLocation('/find-loads')}
                  >
                    Find Loads →
                  </Button>
                </div>
              </div>
              
              {(() => {
                const activeClaims = claimedLoads?.filter(l => l.status !== "Completed" && l.status !== "Closed") || [];
                const pastClaims = claimedLoads?.filter(l => l.status === "Completed" || l.status === "Closed") || [];
                const displayClaims = showPastClaims ? claimedLoads : activeClaims;
                
                if (displayClaims && displayClaims.length > 0) {
                  return (
                    <div className="space-y-4">
                      {displayClaims.slice(0, 5).map(load => (
                        <LoadCard 
                          key={load.id} 
                          load={load}
                          currentUser={currentUser}
                          onUpvote={handleUpvote}
                          upvotingLoadId={upvoteMutation.isPending ? upvoteMutation.variables?.loadId : undefined}
                          onClick={() => handleLoadClick(load)}
                          loadThreads={threadsByLoadId.get(load.id) || []}
                          onNavigate={handleNavigateToShipment}
                        />
                      ))}
                      {pastClaims.length > 0 && !showPastClaims && (
                        <div className="flex items-center gap-2 text-base md:text-sm text-muted-foreground">
                          <span>+ {pastClaims.length} past shipment{pastClaims.length > 1 ? "s" : ""} not shown</span>
                          <span 
                            className="cursor-pointer underline underline-offset-2"
                            onClick={() => setLocation('/history')}
                            data-testid="link-view-history-claims"
                          >
                            View History
                          </span>
                        </div>
                      )}
                      {showPastClaims && displayClaims.length > 5 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setLocation('/history')}
                          data-testid="button-view-all-claims"
                        >
                          View All in History ({displayClaims.length} total)
                        </Button>
                      )}
                    </div>
                  );
                } else if (activeClaims.length === 0 && pastClaims.length > 0) {
                  return (
                    <Card className="p-6 text-center">
                      <p className="text-muted-foreground mb-2">All claimed loads are completed or closed</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowPastClaims(true)}
                        data-testid="button-view-completed-claims"
                      >
                        View Past Shipments
                      </Button>
                    </Card>
                  );
                } else {
                  return (
                    <Card className="p-8 text-center">
                      <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="font-semibold mb-2">No claimed loads</h3>
                      <p className="text-base md:text-sm text-muted-foreground mb-4">Browse available loads and claim one to get started</p>
                      <Button onClick={() => setLocation('/find-loads')} data-testid="button-browse-loads">
                        Browse Loads
                      </Button>
                    </Card>
                  );
                }
              })()}
            </div>

            <div id="section-my-trucks">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <h2 className="text-xl font-semibold">My Trucks</h2>
                <div className="flex items-center gap-2">
                  {userTrucks && userTrucks.some(t => t.status === "Closed") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowClosedTrucks(!showClosedTrucks)}
                      data-testid="button-toggle-closed-trucks"
                    >
                      {showClosedTrucks ? "Hide Closed" : "Show Closed"}
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    data-testid="button-post-truck"
                    onClick={() => setLocation('/post-truck')}
                  >
                    Post Truck →
                  </Button>
                </div>
              </div>
              
              {(() => {
                const activeTrucks = userTrucks?.filter(t => t.status !== "Closed") || [];
                const closedTrucks = userTrucks?.filter(t => t.status === "Closed") || [];
                const displayTrucks = showClosedTrucks ? userTrucks : activeTrucks;
                
                if (displayTrucks && displayTrucks.length > 0) {
                  return (
                    <div className="space-y-4">
                      {displayTrucks.slice(0, 5).map(truck => (
                        <TruckCardSmall 
                          key={truck.id} 
                          truck={truck} 
                          onClick={() => handleTruckClick(truck)}
                        />
                      ))}
                      {closedTrucks.length > 0 && !showClosedTrucks && (
                        <div className="flex items-center gap-2 text-base md:text-sm text-muted-foreground">
                          <span>+ {closedTrucks.length} closed truck{closedTrucks.length > 1 ? "s" : ""} not shown</span>
                          <span 
                            className="cursor-pointer underline underline-offset-2"
                            onClick={() => setLocation('/history')}
                            data-testid="link-view-history-trucks"
                          >
                            View History
                          </span>
                        </div>
                      )}
                      {showClosedTrucks && displayTrucks.length > 5 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setLocation('/history')}
                          data-testid="button-view-all-trucks"
                        >
                          View All in History ({displayTrucks.length} total)
                        </Button>
                      )}
                    </div>
                  );
                } else if (activeTrucks.length === 0 && closedTrucks.length > 0) {
                  return (
                    <Card className="p-6 text-center">
                      <p className="text-muted-foreground mb-2">All truck spaces are closed</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowClosedTrucks(true)}
                        data-testid="button-view-closed"
                      >
                        View Closed Trucks
                      </Button>
                    </Card>
                  );
                } else {
                  return (
                    <Card className="p-8 text-center">
                      <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="font-semibold mb-2">No truck space posted yet</h3>
                      <p className="text-base md:text-sm text-muted-foreground mb-4">Post available truck capacity</p>
                      <Button onClick={() => setLocation('/post-truck')} data-testid="button-create-truck">
                        Post Truck Space
                      </Button>
                    </Card>
                  );
                }
              })()}
            </div>
          </>
        )}
      </div>

      <div id="section-analytics">
        <AnalyticsDashboard 
          userLoads={userLoads} 
          userTrucks={userTrucks} 
          claimedLoads={claimedLoads}
          userType={userType || 'Shipper'}
        />
      </div>

      {userType !== 'Shipper' && completedShipmentsData !== undefined && (
        <Card className="p-4 mb-8" data-testid="card-completed-shipments">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold">Completed Shipments</h3>
              <Badge variant="secondary">{completedShipmentsData.summary.total}</Badge>
              {completedShipmentsData.summary.averageGiven !== null && (
                <div className="flex items-center gap-1 ml-1">
                  <Star className="w-4 h-4 text-orange-400 fill-orange-400" />
                  <span className="text-sm text-muted-foreground">{completedShipmentsData.summary.averageGiven.toFixed(1)} avg given</span>
                </div>
              )}
            </div>
            {completedShipmentsData.shipments.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocation('/history')}
                data-testid="button-view-all-history"
              >
                View All History →
              </Button>
            )}
          </div>
          {completedShipmentsData.shipments.length === 0 ? (
            <p className="text-base md:text-sm text-muted-foreground" data-testid="text-no-completed-shipments">
              Your completed shipments will appear here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {completedShipmentsData.shipments.slice(0, 4).map((shipment) => (
                <div
                  key={shipment.loadId}
                  className="p-3 rounded-md bg-muted/40 space-y-1"
                  data-testid={`card-completed-shipment-${shipment.loadId}`}
                >
                  <div className="flex items-center justify-between gap-1 flex-wrap">
                    <span className="text-sm font-medium truncate">{shipment.route}</span>
                    {shipment.loadIdentifier && (
                      <span className="text-xs text-muted-foreground font-mono">{shipment.loadIdentifier}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{shipment.otherPartyName} · {shipment.otherPartyType}</p>
                  {shipment.completedAt && (
                    <p className="text-xs text-muted-foreground">
                      {new Date(shipment.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                  {shipment.rating !== null ? (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          className={`w-3 h-3 ${n <= (shipment.rating ?? 0) ? 'text-orange-400 fill-orange-400' : 'text-muted-foreground'}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-amber-600 dark:text-amber-400">Not yet rated</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {showPartnersBanner && (
        <Card className="p-4 mt-6 mx-4 mb-2" data-testid="card-partners-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Get more from Fill The Trucks — add your freight partners</p>
                <p className="text-base md:text-sm text-muted-foreground">
                  Your partners won't know to bid until you add them. Go to My Partners to add your brokers and carriers — it only takes a minute.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation('/preferred-partners')}
                data-testid="button-partners-banner-cta"
              >
                Add Partners →
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPartnersBannerDismissed(true)}
                data-testid="button-partners-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      <LoadDetailModal
        load={selectedLoad}
        open={modalOpen}
        onOpenChange={setModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.userType || undefined}
        currentUserVerified={isUserVerified}
        onLoadStatusChange={() => {
          queryClient.invalidateQueries({ queryKey: ['/api/loads/user', userId] });
          queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
        }}
        onEditLoad={(load) => {
          setModalOpen(false);
          handleEditLoad(load);
        }}
      />

      <TruckDetailModal
        truck={selectedTruck}
        open={truckModalOpen}
        onOpenChange={setTruckModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.userType || undefined}
        currentUserVerified={isUserVerified}
        onTruckStatusChange={handleTruckStatusChange}
      />

      <EditLoadModal
        load={editingLoad}
        open={editLoadModalOpen}
        onOpenChange={setEditLoadModalOpen}
        onSuccess={handleEditLoadSuccess}
      />

      <Dialog open={addPartnerModalOpen} onOpenChange={(open) => {
        setAddPartnerModalOpen(open);
        if (!open) {
          setAddPartnerName("");
          setAddPartnerEmail("");
          setAddPartnerType("carrier");
          setAddPartnerSelectedId(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a Partner to Bid on This Load</DialogTitle>
            <DialogDescription>
              Select an existing partner or invite someone new.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Section 1 — Existing partners */}
            {Array.isArray(preferredPartners) && (preferredPartners as PreferredPartner[]).length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Select from My Partners</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(preferredPartners as PreferredPartner[]).map((partner) => (
                    <div
                      key={partner.id}
                      className={`flex items-center justify-between p-2 rounded-md border cursor-pointer transition-colors ${
                        addPartnerSelectedId === partner.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                      onClick={() => {
                        if (addPartnerSelectedId === partner.id) {
                          setAddPartnerSelectedId(null);
                          setAddPartnerName("");
                          setAddPartnerEmail("");
                          setAddPartnerType("carrier");
                        } else {
                          setAddPartnerSelectedId(partner.id);
                          setAddPartnerName(partner.company ?? "");
                          setAddPartnerEmail(partner.email ?? "");
                          setAddPartnerType(partner.userType === "Broker" ? "broker" : "carrier");
                        }
                      }}
                      data-testid={`partner-select-${partner.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{partner.company}</p>
                        <p className="text-xs text-muted-foreground">{partner.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{partner.userType}</span>
                    </div>
                  ))}
                </div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or invite someone new</span>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2 — Invite form (always visible; pre-filled when a partner is selected) */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Name</label>
              <Input
                placeholder="Full name"
                value={addPartnerName}
                onChange={(e) => setAddPartnerName(e.target.value)}
                data-testid="input-add-partner-name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Email</label>
              <Input
                placeholder="Email address"
                type="email"
                value={addPartnerEmail}
                onChange={(e) => setAddPartnerEmail(e.target.value)}
                data-testid="input-add-partner-email"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Type</label>
              <Select
                value={addPartnerType}
                onValueChange={(v) => setAddPartnerType(v as "carrier" | "broker")}
              >
                <SelectTrigger data-testid="select-add-partner-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="carrier">Carrier</SelectItem>
                  <SelectItem value="broker">Broker</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPartnerModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPartnerToLoad}
              disabled={
                addPartnerLoading ||
                (!addPartnerSelectedId && (!addPartnerEmail || !addPartnerName))
              }
              data-testid="button-submit-add-partner"
            >
              {addPartnerLoading ? "Sending..." : addPartnerSelectedId ? "Notify Partner" : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteLoadDialogOpen} onOpenChange={setDeleteLoadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this load?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the load and all associated data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteLoadTargetId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteLoad}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Load
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
